/**
 * @file: useReservationForm.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * The create-reservation form state machine: the intake details the staff
 * supply (arrival, order tags, mileage, advisor, customer type, remark) on top
 * of the read-only lead context (confirmed items + license plate). Seeds sane
 * defaults from the lead and submits the reservation.
 */

import { useCurrentUserId } from '@euler/app/flows/auth/context';
import { LeadsNavParams } from '@euler/app/flows/leads';
import { useAppLoading } from '@euler/components/loading/Provider';
import { getCustomerTypes } from '@euler/functions/getCustomerTypes';
import { useServiceAgentList } from '@euler/functions/useServiceAgentList';
import { OrgMember } from '@euler/model/entity';
import { CustomerType, ServiceSubjectType } from '@euler/model/enum';
import { LeadDetail } from '@euler/model/lead/types';
import { ReservationItem } from '@euler/model/reservation/ReservationItem';
import { useServiceFactory } from '@euler/services/factory';
import { dateFromValue } from '@euler/utils/datetime';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import addDays from 'date-fns/addDays';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Toast from 'react-native-root-toast';

export type ReservationFormState = ReturnType<typeof useReservationForm>;

export const useReservationForm = (leadId: string, detail: LeadDetail) => {
  const { reservationService } = useServiceFactory();
  const loading = useAppLoading();
  const navigation = useNavigation<StackNavigationProp<LeadsNavParams>>();

  const { lead } = detail;
  const serviceAgentsRaw = useServiceAgentList();
  const serviceAgents = useMemo(
    () => serviceAgentsRaw ?? [],
    [serviceAgentsRaw],
  );
  const currentUserId = useCurrentUserId();

  // The items the customer confirmed they'll fix — what this visit is for. They
  // seed both the read-only booked-items list and the persisted `expectedItems`.
  const confirmedItems = useMemo(
    () =>
      detail.items.filter(i => i.isActive && i.customerDecision === 'will_fix'),
    [detail.items],
  );

  // The arrival defaults to the lead's next-service (target) date — the visit
  // this booking is for — at 9am; falls back to a day out when unset/unreadable.
  // Mileage defaults to the soonest item's service-due odometer, then the
  // vehicle's last-known reading.
  const [arrival, setArrival] = useState<Date>(() => {
    if (lead.targetServiceDate) {
      try {
        const target = dateFromValue(lead.targetServiceDate);
        if (!Number.isNaN(target.getTime())) {
          target.setHours(9, 0, 0, 0);
          return target;
        }
      } catch {
        // fall through to the default below
      }
    }
    return addDays(new Date(), 1);
  });
  const [tags, setTags] = useState<string[]>([]);
  const [mileage, setMileage] = useState<string>(() => {
    const dueMileages = confirmedItems
      .map(i => i.nextServiceMileage)
      .filter((m): m is number => typeof m === 'number' && m > 0);
    const seed = dueMileages.length
      ? Math.min(...dueMileages)
      : lead.currentMileage;
    return seed != null && !Number.isNaN(seed) ? String(seed) : '';
  });
  const [serviceAgent, setServiceAgent] = useState<
    { id: number; name: string } | undefined
  >(undefined);
  const [customerType, setCustomerType] = useState<CustomerType | undefined>(
    () => detail.lastService?.customerType ?? undefined,
  );
  const [remark, setRemark] = useState('');

  // Seed the advisor once the roster loads: prefer the lead's assignee when they
  // hold the service-agent role (i.e. appear in the roster), else the current
  // user when they do, else leave it for the staff to pick. The roster *is* the
  // set of members with the role, so membership is the front-end role check.
  const seededAgent = useRef(false);
  useEffect(() => {
    if (seededAgent.current || serviceAgents.length === 0) return;
    seededAgent.current = true;
    const assignee = serviceAgents.find(m => m.id === lead.assigneeId);
    const me = serviceAgents.find(m => m.userId === currentUserId);
    const chosen = assignee ?? me;
    if (chosen) setServiceAgent({ id: chosen.id, name: chosen.name });
  }, [serviceAgents, lead.assigneeId, currentUserId]);

  // Business-type selection mirrors the create-order screen: the picker (an action
  // sheet) sources its own predefined-tag list, so the form only tracks the
  // chosen tags via add/remove.
  const addTag = useCallback((name: string) => {
    setTags(prev => (prev.includes(name) ? prev : [...prev, name]));
  }, []);
  const removeTag = useCallback((name: string) => {
    setTags(prev => prev.filter(x => x !== name));
  }, []);

  const toggleCustomerType = useCallback((type: CustomerType) => {
    setCustomerType(prev => (prev === type ? undefined : type));
  }, []);

  const onPickAgent = useCallback(() => {
    navigation.push('_serviceAgentSelector', {
      selectedServiceAgentId: serviceAgent?.id,
      onSelect: (member: OrgMember) =>
        setServiceAgent({ id: member.id, name: member.name }),
    });
  }, [navigation, serviceAgent?.id]);

  const onSubmit = useCallback(async () => {
    // Required fields must be supplied before the reservation can be booked.
    if (!tags.length) {
      Toast.show('请选择业务类型', { position: Toast.positions.CENTER });
      return;
    }
    if (!serviceAgent) {
      Toast.show('请选择服务顾问', { position: Toast.positions.CENTER });
      return;
    }
    await loading.showWithAction(async () => {
      const mileageNum = mileage.trim() ? Number(mileage) : null;
      // Seed the structured items from the lead's confirmed items as Other
      // service subjects (order-ready, no assignee yet); staff refine via the
      // catalog picker on the detail screen.
      const items: ReservationItem[] = confirmedItems.map(i => ({
        type: ServiceSubjectType.Other,
        name: i.customLabel || i.name,
      }));
      const { reservationId } = await reservationService.create({
        leadId,
        expectedArrivalAt: arrival.toISOString(),
        items,
        tags: tags.join(','),
        vehicleMileage:
          mileageNum != null && !Number.isNaN(mileageNum) ? mileageNum : null,
        serviceAgentId: serviceAgent.id,
        serviceAgentName: serviceAgent.name,
        customerType: customerType ?? null,
        remark: remark.trim() || null,
      });
      Toast.show('已创建预约', { position: Toast.positions.BOTTOM });
      // Replace this form with the created reservation, so the user lands on it
      // and Back returns to the lead (not the now-stale form).
      navigation.replace('_reservation', { id: reservationId });
    });
  }, [
    arrival,
    confirmedItems,
    customerType,
    leadId,
    loading,
    mileage,
    navigation,
    remark,
    reservationService,
    serviceAgent,
    tags,
  ]);

  return {
    confirmedItems,
    licensePlateNo: lead.licensePlateNo,
    arrival,
    setArrival,
    tags,
    addTag,
    removeTag,
    mileage,
    setMileage,
    serviceAgent,
    onPickAgent,
    customerTypes: getCustomerTypes(),
    customerType,
    toggleCustomerType,
    remark,
    setRemark,
    onSubmit,
  };
};
